import React, { createContext, useContext, useLayoutEffect, useRef, useState } from "react";
import { PageHeaderStack } from "../../components/PageHeaderStack.jsx";
import "./ProjectsScrollLayout.css";
import { OverflowTable } from "../../v2/components/ListOverflow.jsx";
import { TABLE_TOP_CORNERS, TABLE_HEADER_CLIP } from "../../v2/components/tableHeaderCorners.js";
import { CornerTable } from "../../v2/components/CornerTable.jsx";

const ProjectsScrollContext = createContext(false);
export const useProjectsScrollLayout = () => useContext(ProjectsScrollContext);

// Only Projects owns this policy; Home's shared list and other objects retain
// their existing header behavior. No KPI measurements participate in offsets.
export function ProjectsScrollLayout({ children }) {
  return (
    <ProjectsScrollContext.Provider value>
      <PageHeaderStack className="projects-header-stack">{children}</PageHeaderStack>
    </ProjectsScrollContext.Provider>
  );
}

// The sticky band must be OUTSIDE the horizontal overflow element: overflow-x
// auto also establishes a vertical scroll container, even without a scrollbar.
export function ProjectsHeaderSurface({ header, children, testId, style, overflowAffordance = true }) {
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const sync = (source, target) => {
    if (target.current && target.current.scrollLeft !== source.currentTarget.scrollLeft) {
      target.current.scrollLeft = source.currentTarget.scrollLeft;
    }
  };
  return (
    <div data-testid={testId} style={{ minWidth: 0, ...style, ...TABLE_TOP_CORNERS }}>
      <div className="projects-data-header" data-testid={`${testId}-header`} style={TABLE_TOP_CORNERS}>
        <div ref={headerRef} className="projects-header-scroll" style={TABLE_HEADER_CLIP} onScroll={(e) => sync(e, bodyRef)}>{React.isValidElement(header) && header.type === "table" ? (overflowAffordance ? <OverflowTable {...header.props} overflowScrollRef={bodyRef} /> : <CornerTable {...header.props} />) : header}</div>
      </div>
      <div ref={bodyRef} data-testid={`${testId}-body`} className="projects-body-scroll" onScroll={(e) => sync(e, headerRef)}>{children}</div>
    </div>
  );
}

// Preserve the native table's auto-sizing (including wrapped text and colspans)
// with an inert, collapsed sizing header. The visible, interactive header uses
// the measured column widths and stays a single band for multirow headers.
function sizingCopy(node) {
  if (!React.isValidElement(node)) return node;
  const props = Object.fromEntries(Object.entries(node.props).filter(([key]) =>
    !key.startsWith("on") && !key.startsWith("data-") && key !== "id" && key !== "children"));
  return React.createElement(node.type, { ...props, key: node.key },
    React.Children.map(node.props.children, sizingCopy));
}

export function ProjectsTableScroll({ children: table, style, testId = "projects-table-scroll", overflowAffordance = true }) {
  const enabled = useProjectsScrollLayout();
  const sizingRef = useRef(null);
  const [widths, setWidths] = useState([]);
  const parts = React.Children.toArray(table.props.children);
  const head = parts.find((child) => child.type === "thead");
  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const element = sizingRef.current;
    const measure = () => {
      const cells = element?.querySelector("thead tr:last-child")?.children || [];
      const next = Array.from(cells, (cell) => cell.getBoundingClientRect().width);
      setWidths((prev) => JSON.stringify(prev) === JSON.stringify(next) ? prev : next);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (element) observer?.observe(element);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [enabled, table]);
  if (!enabled) return <div data-testid={testId} style={{ overflowX: "auto", ...style, ...TABLE_TOP_CORNERS }}>{overflowAffordance ? <OverflowTable {...table.props} /> : <CornerTable {...table.props} />}</div>;
  const width = widths.reduce((sum, value) => sum + value, 0);
  const headerStyle = { ...table.props.style, ...(width > 0 ? { width, minWidth: width, tableLayout: "fixed" } : {}) };
  return (
    <ProjectsHeaderSurface testId={testId} style={style} overflowAffordance={overflowAffordance}
      header={<table style={headerStyle}>
        <colgroup>{widths.map((value, i) => <col key={i} style={{ width: value }} />)}</colgroup>
        {head}
      </table>}>
      {React.cloneElement(table, { ref: sizingRef }, parts.map((part) => part === head
        ? <thead key="sizing-header" aria-hidden="true" inert="" style={{ visibility: "collapse" }}>{React.Children.map(head.props.children, sizingCopy)}</thead>
        : part))}
    </ProjectsHeaderSurface>
  );
}
